import React from 'react';
import { useCurrency } from '../contexts/CurrencyContext';

/**
 * Prominently show a price in the visitor's preferred currency, with a clear
 * dark-grey "charged as €X" line underneath whenever a conversion happened.
 *
 * Props:
 *   amount          — the raw number stored on the item (e.g. 15)
 *   currency        — the item's native currency (lowercase ISO)
 *   className       — passed to the primary amount span
 *   noteClassName   — tailwind overrides for the "charged as" line
 *   inline          — when true, render the note on the same line (useful inside buttons)
 *   showNote        — set false to suppress the "charged as" note entirely
 */
export default function DisplayPrice({
    amount,
    currency,
    className = '',
    noteClassName = '',
    inline = false,
    showNote = true,
}) {
    const { priceDisplay } = useCurrency();
    const { display, original, hasConversion } = priceDisplay(amount, currency);

    const noteCls = `text-xs text-[#4A4A68] ${noteClassName}`.trim();

    if (inline) {
        return (
            <span className={className}>
                {display}
                {hasConversion && showNote && (
                    <span className={` ${noteCls} ml-1`}>· charged as {original}</span>
                )}
            </span>
        );
    }

    return (
        <span className={`inline-flex flex-col items-start ${className}`}>
            <span>{display}</span>
            {hasConversion && showNote && (
                <span className={noteCls} data-testid="price-charge-note">
                    charged as {original}
                </span>
            )}
        </span>
    );
}
